import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Download,
  Plus, Loader2, X,
} from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  addYears, format, isSameMonth, isSameDay, startOfDay, startOfYear,
  eachMonthOfInterval, endOfYear,
} from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const PRIORITY_COLOR = {
  urgent: "bg-rose-500", high: "bg-amber-500",
  medium: "bg-blue-500", low: "bg-slate-400", important: "bg-violet-500",
};

export default function Calendar() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";
  const locale = lang === "es" ? es : enUS;

  const [view, setView] = useState("month"); // day | week | month | year
  const [cursor, setCursor] = useState(new Date());
  const [creatingOnDate, setCreatingOnDate] = useState(null);

  const range = useMemo(() => {
    if (view === "day")   return { s: startOfDay(cursor), e: startOfDay(cursor) };
    if (view === "week")  return { s: startOfWeek(cursor, { weekStartsOn: 1 }), e: endOfWeek(cursor, { weekStartsOn: 1 }) };
    if (view === "month") return { s: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), e: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }) };
    return { s: startOfYear(cursor), e: endOfYear(cursor) };
  }, [view, cursor]);

  const { data, isLoading } = useQuery({
    queryKey: ["calendar-events", format(range.s, "yyyy-MM-dd"), format(range.e, "yyyy-MM-dd")],
    queryFn: async () => (await api.get(`/calendar/events?start=${format(range.s, "yyyy-MM-dd")}&end=${format(range.e, "yyyy-MM-dd")}`)).data,
  });
  const events = data?.events || [];

  function eventsOn(day) {
    return events.filter(ev => ev.due_date && isSameDay(new Date(ev.due_date), day));
  }

  const step = (dir) => {
    if (view === "day")   setCursor(addDays(cursor, dir));
    else if (view === "week")  setCursor(addWeeks(cursor, dir));
    else if (view === "month") setCursor(addMonths(cursor, dir));
    else setCursor(addYears(cursor, dir));
  };

  const title = view === "year"
    ? format(cursor, "yyyy")
    : view === "month"
    ? format(cursor, "MMMM yyyy", { locale })
    : view === "week"
    ? `${format(range.s, "d MMM", { locale })} — ${format(range.e, "d MMM yyyy", { locale })}`
    : format(cursor, "EEEE, d MMMM yyyy", { locale });

  async function downloadIcs() {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const token = (document.cookie.match(/session_token=([^;]+)/) || [])[1];
    try {
      const res = await fetch(`${backend}/api/calendar/ics`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "sprintroll.ics";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === "es" ? "Archivo .ics descargado — impórtalo en tu calendario" : ".ics downloaded — import into your calendar");
    } catch (e) {
      toast.error(e.message || "Error");
    }
  }

  function openGoogleCalendarImport() {
    // Google doesn't accept a direct raw ICS URL without OAuth; guide the user.
    toast.message(
      lang === "es"
        ? "Descarga el archivo .ics y súbelo desde calendar.google.com → Configuración → Importar"
        : "Download the .ics and upload it at calendar.google.com → Settings → Import",
    );
    downloadIcs();
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="calendar-page">
      {/* Header */}
      <header className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-6 w-6 text-blue-500" />
          <h1 className="font-display text-2xl font-semibold capitalize" data-testid="calendar-title">{title}</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setCursor(new Date())} data-testid="cal-today">
            {lang === "es" ? "Hoy" : "Today"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => step(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => step(+1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <div className="glass-panel rounded-full p-0.5 inline-flex" data-testid="cal-view-switcher">
            {[
              { v: "day",   l: lang === "es" ? "Día" : "Day" },
              { v: "week",  l: lang === "es" ? "Semana" : "Week" },
              { v: "month", l: lang === "es" ? "Mes" : "Month" },
              { v: "year",  l: lang === "es" ? "Año" : "Year" },
            ].map(o => (
              <button
                key={o.v}
                onClick={() => setView(o.v)}
                className={cn("px-3 h-8 rounded-full text-xs font-medium transition", view === o.v ? "bg-blue-600 text-white" : "hover:bg-white/40 dark:hover:bg-white/10")}
                data-testid={`cal-view-${o.v}`}
              >{o.l}</button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={downloadIcs} data-testid="cal-export-apple" title="Apple Calendar / Outlook (.ics)">
            <Download className="h-4 w-4 mr-1.5" /> Apple / Outlook
          </Button>
          <Button size="sm" variant="outline" onClick={openGoogleCalendarImport} data-testid="cal-export-google" title="Google Calendar">
            <Download className="h-4 w-4 mr-1.5" /> Google
          </Button>
        </div>
      </header>

      {isLoading && <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>}

      {!isLoading && view === "month" && (
        <MonthGrid cursor={cursor} range={range} events={events} eventsOn={eventsOn} onDayClick={setCreatingOnDate} locale={locale} lang={lang} />
      )}
      {!isLoading && view === "week" && (
        <WeekGrid range={range} eventsOn={eventsOn} onDayClick={setCreatingOnDate} locale={locale} />
      )}
      {!isLoading && view === "day" && (
        <DayView day={cursor} events={eventsOn(cursor)} onCreate={() => setCreatingOnDate(cursor)} locale={locale} lang={lang} />
      )}
      {!isLoading && view === "year" && (
        <YearGrid year={cursor} events={events} onMonthClick={(d) => { setCursor(d); setView("month"); }} locale={locale} />
      )}

      {creatingOnDate && (
        <CreateCardDialog
          date={creatingOnDate}
          onClose={() => setCreatingOnDate(null)}
          onCreated={(cardId, boardId) => {
            setCreatingOnDate(null);
            navigate(`/boards/${boardId}`);
          }}
        />
      )}
    </div>
  );
}

/* ────── Month Grid ────── */
function MonthGrid({ cursor, range, events, eventsOn, onDayClick, locale, lang }) {
  const days = [];
  let d = range.s;
  while (d <= range.e) { days.push(d); d = addDays(d, 1); }
  const weekdayLabels = Array.from({ length: 7 }, (_, i) => format(addDays(range.s, i), "EEE", { locale }));
  return (
    <div className="glass-panel rounded-2xl overflow-hidden" data-testid="cal-month-grid">
      <div className="grid grid-cols-7 border-b border-white/10 text-xs font-semibold text-muted-foreground">
        {weekdayLabels.map((wd, i) => <div key={i} className="px-2 py-2 text-center capitalize">{wd}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const outside = !isSameMonth(day, cursor);
          const today = isSameDay(day, new Date());
          const dayEvents = eventsOn(day);
          return (
            <button
              key={i}
              onClick={() => onDayClick(day)}
              data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
              className={cn(
                "min-h-[110px] p-1.5 text-left border-r border-b border-white/5 hover:bg-blue-500/5 transition group relative",
                outside && "opacity-40",
              )}
            >
              <div className="flex items-center justify-between">
                <span className={cn("text-xs font-semibold rounded-full h-6 w-6 grid place-items-center", today && "bg-blue-600 text-white")}>
                  {format(day, "d")}
                </span>
                <span className="opacity-0 group-hover:opacity-100 transition"><Plus className="h-3 w-3 text-blue-500" /></span>
              </div>
              <div className="mt-1 space-y-0.5">
                {dayEvents.slice(0, 3).map(ev => (
                  <div key={ev.card_id} className={cn("truncate rounded px-1.5 py-0.5 text-[10px] text-white", PRIORITY_COLOR[ev.priority] || "bg-blue-500")}
                       title={`${ev.title} — ${ev.board_name}`}>
                    {ev.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] text-muted-foreground">+{dayEvents.length - 3} más</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ────── Week Grid ────── */
function WeekGrid({ range, eventsOn, onDayClick, locale }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(range.s, i));
  return (
    <div className="glass-panel rounded-2xl overflow-hidden grid grid-cols-7" data-testid="cal-week-grid">
      {days.map((day, i) => {
        const today = isSameDay(day, new Date());
        const ev = eventsOn(day);
        return (
          <button key={i} onClick={() => onDayClick(day)}
                  data-testid={`cal-week-day-${format(day, "yyyy-MM-dd")}`}
                  className="p-3 text-left border-r border-white/5 last:border-r-0 hover:bg-blue-500/5 min-h-[400px]">
            <div className="text-[11px] uppercase font-semibold text-muted-foreground">{format(day, "EEE", { locale })}</div>
            <div className={cn("text-2xl font-display font-semibold mt-0.5", today && "text-blue-600")}>
              {format(day, "d")}
            </div>
            <div className="mt-3 space-y-1.5">
              {ev.map(e => (
                <div key={e.card_id} className={cn("rounded-lg text-[11px] text-white px-2 py-1.5", PRIORITY_COLOR[e.priority] || "bg-blue-500")}>
                  <div className="font-semibold truncate">{e.title}</div>
                  <div className="text-[9px] opacity-80 truncate">{e.board_name}</div>
                </div>
              ))}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ────── Day View ────── */
function DayView({ day, events, onCreate, lang }) {
  return (
    <div className="glass-panel rounded-2xl p-6" data-testid="cal-day-view">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {events.length} {lang === "es" ? "evento(s)" : "event(s)"}
        </p>
        <Button size="sm" onClick={onCreate} data-testid="cal-day-create">
          <Plus className="h-4 w-4 mr-1" /> {lang === "es" ? "Nueva tarjeta" : "New card"}
        </Button>
      </div>
      {events.length === 0 ? (
        <p className="text-center py-10 text-sm text-muted-foreground">
          {lang === "es" ? "Sin eventos programados este día." : "No events scheduled for this day."}
        </p>
      ) : (
        <ul className="space-y-2">
          {events.map(e => (
            <li key={e.card_id} className="glass-card rounded-xl p-3 flex items-center gap-3">
              <div className={cn("h-10 w-1 rounded", PRIORITY_COLOR[e.priority] || "bg-blue-500")} />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{e.title}</div>
                <div className="text-xs text-muted-foreground">{e.board_name} · {format(new Date(e.due_date), "HH:mm")}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ────── Year View ────── */
function YearGrid({ year, events, onMonthClick, locale }) {
  const months = eachMonthOfInterval({ start: startOfYear(year), end: endOfYear(year) });
  return (
    <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cal-year-grid">
      {months.map(m => {
        const days = [];
        const s = startOfWeek(startOfMonth(m), { weekStartsOn: 1 });
        const e = endOfWeek(endOfMonth(m), { weekStartsOn: 1 });
        let d = s;
        while (d <= e) { days.push(d); d = addDays(d, 1); }
        const monthEvents = events.filter(ev => ev.due_date && isSameMonth(new Date(ev.due_date), m));
        return (
          <button key={m.toISOString()} onClick={() => onMonthClick(m)}
                  className="glass-panel rounded-2xl p-3 hover:ring-2 hover:ring-blue-500/40 transition text-left"
                  data-testid={`cal-year-month-${format(m, "yyyy-MM")}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold capitalize text-sm">{format(m, "MMMM", { locale })}</div>
              <div className="text-[10px] rounded-full bg-blue-500/15 text-blue-600 px-2 py-0.5 font-semibold">{monthEvents.length}</div>
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {days.map((d, i) => {
                const outside = !isSameMonth(d, m);
                const has = monthEvents.some(ev => isSameDay(new Date(ev.due_date), d));
                return (
                  <div key={i} className={cn("aspect-square rounded text-[9px] grid place-items-center",
                    outside ? "opacity-30" : "",
                    has ? "bg-blue-500/30 text-blue-700 dark:text-blue-300 font-semibold" : "text-muted-foreground",
                  )}>{format(d, "d")}</div>
                );
              })}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ────── Create Card Dialog ────── */
function CreateCardDialog({ date, onClose, onCreated }) {
  const qc = useQueryClient();
  const [boardId, setBoardId] = useState("");
  const [listId, setListId] = useState("");
  const [title, setTitle] = useState("");

  const { data: boardsRecent } = useQuery({
    queryKey: ["boards-for-cal"],
    queryFn: async () => (await api.get("/boards/recent")).data,
  });
  const { data: boardFull } = useQuery({
    queryKey: ["board-lists-cal", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/full`)).data,
    enabled: !!boardId,
  });

  const createMutation = useMutation({
    mutationFn: async () => (await api.post(`/lists/${listId}/cards`, {
      title: title.trim(),
      due_date: date.toISOString(),
    })).data,
    onSuccess: (card) => {
      toast.success("Tarjeta creada");
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      onCreated?.(card.card_id, boardId);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const canSubmit = boardId && listId && title.trim();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cal-create-dialog">
        <DialogHeader>
          <DialogTitle>Nueva tarjeta · {format(date, "d 'de' MMMM yyyy", { locale: es })}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1">
            <Label>Tablero</Label>
            <Select value={boardId} onValueChange={(v) => { setBoardId(v); setListId(""); }}>
              <SelectTrigger data-testid="cal-create-board"><SelectValue placeholder="Elige un tablero" /></SelectTrigger>
              <SelectContent>
                {(boardsRecent || []).map(b => <SelectItem key={b.board_id} value={b.board_id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {boardId && (
            <div className="space-y-1">
              <Label>Lista</Label>
              <Select value={listId} onValueChange={setListId}>
                <SelectTrigger data-testid="cal-create-list"><SelectValue placeholder="Elige una lista" /></SelectTrigger>
                <SelectContent>
                  {(boardFull?.lists || []).map(l => <SelectItem key={l.list_id} value={l.list_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label>Título</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título de la tarjeta" autoFocus data-testid="cal-create-title" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => createMutation.mutate()} disabled={!canSubmit || createMutation.isPending} data-testid="cal-create-submit">
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
